import type {
  BookDocType,
  DeprecatedBookDocType,
  InsertAbleBookDocType,
} from "@oboku/shared"
import type {
  MigrationStrategies,
  RxCollection,
  RxDocument,
  RxJsonSchema,
} from "rxdb"
import { getReplicationProperties } from "../replication/getReplicationProperties"
import { generateId } from "./utils"

// biome-ignore lint/complexity/noBannedTypes: TODO
type BookDocMethods = {}

type BookDocument = RxDocument<BookDocType, BookDocMethods>

type BookCollectionMethods = {
  post: (
    json: Omit<InsertAbleBookDocType, "rx_model" | `rxdbMeta`>,
  ) => Promise<BookDocument>
}

export type BookCollection = RxCollection<
  BookDocType,
  BookDocMethods,
  BookCollectionMethods
>

export const bookDocMethods: BookDocMethods = {}

export const bookCollectionMethods: BookCollectionMethods = {
  post: async function (this: BookCollection, json) {
    return this.insert({ _id: generateId(), ...json } as BookDocType)
  },
}

export const bookSchemaMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: Record<string, unknown>) => oldDoc,
  2: (oldDoc: Record<string, unknown>) => oldDoc,
  // v3: added optional `metadataSourcePriority`; nothing to backfill since
  // the field is optional and the merge falls back to the default order.
  3: (oldDoc: Record<string, unknown>) => oldDoc,
  // v4: added optional `bucketCoverKey` marker recording what was last
  // uploaded to the bucket; left undefined for existing docs so the next
  // metadata refresh re-uploads once and populates it.
  4: (oldDoc: Record<string, unknown>) => oldDoc,
  // v5: added optional `readingStateUpdatedAt`, superseding
  // `readingStateCurrentBookmarkProgressUpdatedAt`, and optional
  // `readingStateReachedProgressPercent`; nothing to backfill since the
  // activity order reads the later of the two dates, and the shown progress
  // falls back to the bookmark progress.
  5: (oldDoc: Record<string, unknown>) => oldDoc,
  // v6: added optional `readingStateCurrentBookmarkKoreaderProgress`; nothing
  // to backfill since the reader writes it the next time the book is opened.
  6: (oldDoc: Record<string, unknown>) => oldDoc,
  // v7: added optional `readingStateCurrentBookmarkKoreaderDocument`; nothing
  // to backfill since the reader writes it the next time the book is opened.
  7: (oldDoc: Record<string, unknown>) => oldDoc,
  // v8: added optional `readingStateCurrentBookmarkLocationUpdatedAt`; nothing
  // to backfill since the reader writes it the next time the bookmark moves.
  8: (oldDoc: Record<string, unknown>) => oldDoc,
}

export const bookSchema: RxJsonSchema<
  Omit<BookDocType & DeprecatedBookDocType, `_rev` | `rxdbMeta`>
> = {
  title: "books",
  version: 8,
  type: "object",
  primaryKey: `_id`,
  properties: {
    _id: { type: `string`, maxLength: 100 },
    collections: {
      type: "array",
      ref: "obokucollection",
      items: { type: "string" },
    },
    createdAt: { type: ["number"] },
    lastMetadataUpdatedAt: { type: ["number", "null"] },
    lastMetadataUpdateError: { type: ["string", "null"] },
    metadataUpdateStatus: { type: ["string", "null"] },
    links: { ref: "link", type: "array", items: { type: "string" } },
    readingStateCurrentBookmarkLocation: { type: ["string", "null"] },
    readingStateCurrentBookmarkLocationUpdatedAt: { type: ["string", "null"] },
    readingStateCurrentBookmarkProgressPercent: { type: ["number"] },
    readingStateCurrentBookmarkKoreaderProgress: { type: ["string", "null"] },
    readingStateCurrentBookmarkKoreaderDocument: { type: ["string", "null"] },
    readingStateReachedProgressPercent: { type: ["number"] },
    readingStateUpdatedAt: { type: ["string", "null"] },
    readingStateCurrentState: { type: ["string"] },
    tags: { type: "array", ref: "tag", items: { type: "string" } },
    modifiedAt: { type: ["string", "null"] },
    isAttachedToDataSource: { type: ["boolean"] },
    isNotInterested: { type: ["boolean"] },
    metadata: { type: ["array"] },
    metadataFetchEnabled: { type: ["boolean", "null"] },
    metadataFileDownloadEnabled: { type: ["boolean", "null"] },
    metadataSourcePriority: { type: ["array"], items: { type: "string" } },
    bucketCoverKey: { type: ["string", "null"] },
    ...getReplicationProperties(`book`),

    // -------------------------------------------------------------------------
    // Legacy properties
    // -------------------------------------------------------------------------
    /**
     * Properties the app no longer writes (`DeprecatedBookDocType`). They stay
     * declared because stored books can still hold them and RxDB rejects
     * undeclared properties. One can only be removed once no CouchDB document
     * holds it, which takes a CouchDB migration first (AGENTS.md, "Data
     * migrations"), not an RxDB one.
     *
     * - `creator`, `date`, `lang`, `publisher`, `rights`, `subject`, `title`:
     *   superseded by the `metadata` entries, and read nowhere. Whether
     *   CouchDB documents still hold them has not been checked.
     * - `readingStateCurrentBookmarkProgressUpdatedAt`: superseded by
     *   `readingStateUpdatedAt`, but still on books unchanged since, written by
     *   older versions of the app, and read by `getReadingStateUpdatedTime`
     *   (see its TODO).
     */
    creator: { type: ["string", "null"] },
    date: { type: ["number", "null"] },
    lang: { type: ["string", "null"] },
    publisher: { type: ["string", "null"] },
    rights: { type: ["string", "null"] },
    subject: { type: ["array", "null"], items: { type: "string" } },
    title: { type: ["string", "null"] },
    readingStateCurrentBookmarkProgressUpdatedAt: {
      type: ["string", "null"],
    },
  },
  required: [],
}
